import { formatPresetDuration } from "../utils/presetDuration";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import CompletionSaveStatus from "./CompletionSaveStatus";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import React, { useLayoutEffect, useRef, useState } from "react";
import RepeatRoundedIcon from "@mui/icons-material/RepeatRounded";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import ChecklistRoundedIcon from "@mui/icons-material/ChecklistRounded";
import BookmarkBorderRoundedIcon from "@mui/icons-material/BookmarkBorderRounded";
import { Button, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Menu, MenuItem, ListItemIcon } from "@mui/material";

const MyPresetList = (props) => {
  const userPandaPresets = props.presets;

  const drag = useRef(null);
  const listRef = useRef(null);
  const settleFrom = useRef(null);
  const [dragOffset, setDragOffset] = useState(0);

  const capturePositions = () => new Map(
    [...(listRef.current?.children || [])].map((row) => [row.dataset.presetId, row.getBoundingClientRect().top])
  );

  // Animate from the last visible position into the committed list order.
  useLayoutEffect(() => {
    if (!settleFrom.current) return;
    const positions = settleFrom.current;
    settleFrom.current = null;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    for (const row of listRef.current?.children || []) {
      const previous = positions.get(row.dataset.presetId);
      if (previous === undefined) continue;
      const offset = previous - row.getBoundingClientRect().top;
      if (offset) row.animate?.([
        { transform: `translateY(${offset}px)` },
        { transform: "translateY(0)" },
      ], { duration: 240, easing: "cubic-bezier(0.22, 1, 0.36, 1)" });
    }
  });

  const finishDrag = (commit = false) => {
    if (!drag.current) return;
    settleFrom.current = capturePositions();
    const { from, to } = drag.current;
    drag.current = null;
    setDropTarget(null);
    setDragOffset(0);
    if (commit) movePreset(from, to);
  };
  const [dropTarget, setDropTarget] = useState(null);
  const [announcement, setAnnouncement] = useState("");
  const [actionMenu, setActionMenu] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [presetToDelete, setPresetToDelete] = useState(null);

  const handleDelete = (preset) => {
    if (props.onDelete(preset)) {
      setOpenDeleteDialog(false);
      setPresetToDelete(null);
    }
  };

  const handleDeleteClick = (preset) => {
    setPresetToDelete(preset);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    if (presetToDelete) {
      handleDelete(presetToDelete);
    }
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    setPresetToDelete(null);
  };

  const visiblePresets = props.dailyOnly
    ? userPandaPresets.filter((preset) => preset.isDailyRoutine === true)
    : userPandaPresets;

  const movePreset = (from, to) => {
    if (from === to || to < 0 || to >= visiblePresets.length) return;
    const ids = visiblePresets.map((preset) => preset.id);
    const [id] = ids.splice(from, 1);
    ids.splice(to, 0, id);
    if (props.onReorder?.(ids) !== false) {
      setAnnouncement(`${visiblePresets[from].presetName} moved to position ${to + 1} of ${ids.length}`);
    }
  };

  return (
    <div className="my-presets-list">
      <span className="my-preset-reorder-announcement" role="status" aria-live="polite">{announcement}</span>
      {props.loading ? (
        <div className="my-presets-status">
          <span className="my-presets-loader" />
          <span>Loading your presets…</span>
        </div>
      ) : userPandaPresets.length === 0 ? (
        <div className="my-presets-empty">
          <span className="my-presets-empty-icon">
            <BookmarkBorderRoundedIcon />
          </span>
          <h3>No saved presets yet</h3>
          <p>Create your first routine. Saved cloud presets load automatically.</p>
          <button onClick={props.startCreating}>Create preset</button>
        </div>
      ) : visiblePresets.length === 0 ? (
        <div className="my-presets-empty">
          <h3>No daily routine presets yet</h3>
          <p>Edit a preset and check “Daily routine”, or create a new one.</p>
          <button onClick={props.startCreating}>Create preset</button>
        </div>
      ) : (
        <ul ref={listRef} className="my-presets-items">
          {visiblePresets.map((preset, index) => (
            <li
              key={preset.id}
              data-preset-id={preset.id}
              style={drag.current ? {
                transform: `translateY(${index === drag.current.from ? dragOffset :
                  index > drag.current.from && index <= dropTarget ? -drag.current.step :
                  index < drag.current.from && index >= dropTarget ? drag.current.step : 0}px)`,
                transition: index === drag.current.from ? "none" : "transform 200ms cubic-bezier(0.22, 1, 0.36, 1)",
                position: "relative",
                zIndex: index === drag.current.from ? 2 : 0,
              } : undefined}
              className={`my-preset-row ${drag.current?.from === index ? "is-dragging" : ""} ${dropTarget === index ? "is-reorder-target" : ""} ${(props.completions?.count(preset.id) || 0) > 0 ? "is-complete" : ""}`}
            >
              {props.onReorder && visiblePresets.length > 1 && (
                <button type="button" className="my-preset-reorder-handle"
                  aria-label={`Reorder ${preset.presetName}`}
                  title="Drag to reorder, or use the up and down arrow keys"
                  onKeyDown={(event) => {
                    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
                      event.preventDefault();
                      settleFrom.current = capturePositions();
                      movePreset(index, index + (event.key === "ArrowUp" ? -1 : 1));
                    }
                  }}
                  onPointerDown={(event) => {
                    if (event.button !== 0) return;
                    const rows = [...listRef.current.children];
                    rows.forEach((row) => row.getAnimations?.().forEach((animation) => animation.cancel()));
                    const rects = rows.map((row) => row.getBoundingClientRect());
                    const step = index < rects.length - 1
                      ? rects[index + 1].top - rects[index].top
                      : rects[index].top - rects[index - 1].top;
                    drag.current = { from: index, to: index, startY: event.clientY, rects, step };
                    setDragOffset(0);
                    event.currentTarget.setPointerCapture(event.pointerId);
                    setDropTarget(index);
                  }}
                  onPointerMove={(event) => {
                    if (!drag.current) return;
                    const current = drag.current;
                    const first = current.rects[0];
                    const last = current.rects[current.rects.length - 1];
                    const origin = current.rects[current.from];
                    const offset = Math.max(first.top - origin.top,
                      Math.min(last.top - origin.top, event.clientY - current.startY));
                    const center = origin.top + (origin.bottom - origin.top) / 2 + offset;
                    let target = 0;
                    current.rects.forEach((rect, rowIndex) => {
                      if (center >= rect.top) target = rowIndex;
                    });
                    current.to = target;
                    setDragOffset(offset);
                    setDropTarget(target);
                  }}
                  onPointerUp={() => finishDrag(true)}
                  onLostPointerCapture={() => finishDrag()}
                  onPointerCancel={() => finishDrag()}>

                  <DragIndicatorRoundedIcon fontSize="small" />
                </button>
              )}
              <div className="my-preset-row-copy">
                <span className="my-preset-row-name">{preset.presetName}</span>
                <span className="my-preset-row-meta">
                  {preset.isDailyRoutine === true && (
                    <span title="Daily routine" aria-label="Daily routine">
                      <RepeatRoundedIcon />
                    </span>
                  )}
                  <span>
                    <ChecklistRoundedIcon />
                    {preset.presetTasks?.length || 0}
                  </span>
                  <span>
                    <AccessTimeRoundedIcon />
                    {formatPresetDuration((preset.presetTasks || []).reduce(
                      (total, task) =>
                        total + Number(task.estimatedTaskTime || 0),
                      0
                    ))}
                  </span>
                </span>
              </div>

              <div className="my-preset-row-actions">
                {preset.isDailyRoutine && (props.completions?.count(preset.id) || 0) > 0 && (
                  <span className="my-preset-daily-count" aria-label={`${props.completions?.count(preset.id) || 0} completions today`}
                    title="Completed today · resets at midnight IST">{props.completions?.count(preset.id) || 0}×</span>
                )}
                {Object.values(props.completions?.pending || {}).filter((event) => event.presetId === preset.id).map((event) =>
                  <CompletionSaveStatus key={event.runId} name={preset.presetName}
                    status={props.completions.status(event.runId)} error={props.completions.errors?.[event.runId]}
                    retry={() => props.completions.retry(event.runId)} />
                )}
                <button
                  onClick={() => {
                    props.addTasksToList(preset);
                    props.setIsMenuOn(false);
                  }}
                  className="my-preset-use-button"
                >
                  <RocketLaunchIcon />
                  <span>Use</span>
                </button>
                <button type="button" className="my-preset-icon-button"
                  aria-label={`More actions for ${preset.presetName}`} aria-haspopup="menu"
                  aria-expanded={actionMenu?.preset.id === preset.id}
                  onClick={(event) => setActionMenu({ anchor: event.currentTarget, preset })}>
                  <MoreVertRoundedIcon />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Menu anchorEl={actionMenu?.anchor || null} open={Boolean(actionMenu)} onClose={() => setActionMenu(null)}>
        <MenuItem aria-label={`Edit ${actionMenu?.preset.presetName || "preset"}`} onClick={() => {
          const preset = actionMenu.preset;
          setActionMenu(null);
          props.handleEdit(preset);
        }}><ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>Edit</MenuItem>
        {actionMenu?.preset.isDailyRoutine && <MenuItem aria-label={`Reset today's count for ${actionMenu.preset.presetName}`} onClick={() => {
          const preset = actionMenu.preset;
          setActionMenu(null);
          props.completions.reset(preset.id);
        }}><ListItemIcon><RestartAltRoundedIcon fontSize="small" /></ListItemIcon>Reset today’s count</MenuItem>}
        <MenuItem aria-label={`Delete ${actionMenu?.preset.presetName || "preset"}`} onClick={() => {
          const preset = actionMenu.preset;
          setActionMenu(null);
          handleDeleteClick(preset);
        }} sx={{ color: "error.main" }}><ListItemIcon><DeleteOutlineIcon fontSize="small" color="error" /></ListItemIcon>Delete</MenuItem>
      </Menu>
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCancelDelete}
        className="delete-preset-dialog"
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Delete Preset
        </DialogTitle>
        <DialogContent>
          <Typography id="alert-dialog-description">
            Are you sure you want to delete the preset "<strong>{presetToDelete?.presetName}</strong>"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MyPresetList;
